/* eslint no-unused-vars: "off" */
import React, { useContext, useState } from 'react';
import { AnalyticsContext } from '@carvana/eventable-clickstream';
import { useIntersectionObserver } from 'usehooks-ts';
import { datadogRum } from '@datadog/browser-rum';
import ConcernReportedModal from './concern-reported-modal';
import ReportConcernModal from './report-concern-modal';

import {
  ASRCardWrapper,
  MainContentWrapper,
  ContentWrapper,
  Button,
  TextContainer,
  Text,
  Note,
  HR,
  FootNoteWrapper,
  ConcernsWrapper,
  ConcernList,
  ConcernListItem,
  Accordion,
} from './ASRCard.styles';
import { HeaderTitle, HeaderIcon, HeaderText } from '../_common/card/Card.styles';
import VehicleIssuesIcon from '../../assets/VehicleIssuesIcon';
import ChevronIcon from '../../assets/ChevronIcon';
import { formatDate } from '../../services/UtilitiesService';
import { DateMasks, AsrAnalytics } from '../../constants';

/* After Sale Repair Card */
const ASRCard = ({ feedData }: any) => {
  const { trackEvent } = useContext(AnalyticsContext) as any;
  const [selectedConcern, setSelectedConcern] = useState<any>({});

  const {
    title,
    content: { reportedConcerns },
  } = feedData;

  const [isAccordionOpen, setIsAccordionOpen] = useState(false);

  const [asrId, setAsrId] = useState(0);
  const [shouldShowReportedConcernsModal, setShouldShowReportedConcernsModal] = useState(false);
  const [shouldShowCreateConcernModal, setShouldShowCreateConcernModal] = useState(false);

  // Launch ASR Form
  const launchASRForm = () => {
    trackEvent(AsrAnalytics.CLICKED_GET_STARTED);
    setShouldShowCreateConcernModal(true);
  };

  const captureImpression = () => {
    trackEvent(AsrAnalytics.VIEWED);
    datadogRum.addAction(AsrAnalytics.VIEWED);
  };
  const { ref } = useIntersectionObserver({
    freezeOnceVisible: true,
    threshold: 0.5,
    onChange: (isIntersecting: boolean) => {
      if (isIntersecting && captureImpression) captureImpression();
    },
  });

  // Handle concern click
  const handleConcernClick = (concernData: any) => {
    const concernId = concernData.parentId;
    setAsrId(concernId);
    setSelectedConcern(concernData);
    setShouldShowReportedConcernsModal(true);
    trackEvent(AsrAnalytics.CLICKED_CONCERN, { concernId });
    datadogRum.addAction(AsrAnalytics.CLICKED_CONCERN, { concernId });
  };

  // Expand / Collapse handler
  const toggleStatusHandler = (isOpen: boolean) => {
    setIsAccordionOpen(isOpen);

    if (isOpen) {
      trackEvent(AsrAnalytics.EXPANDED_REPORTED_CONCERNS);
    }
  };

  const fourteenDaysAgo = new Date(new Date().getTime() - 1209600000);
  const reportedConcernsWithin14Days = reportedConcerns.filter(
    (asrIssue: any) => new Date(asrIssue.createdOn) >= fourteenDaysAgo
  );
  const hasConcerns = reportedConcernsWithin14Days.length > 0;

  const showFootnote = isAccordionOpen && hasConcerns;

  return (
    <>
      <ASRCardWrapper data-testid="asr-card-wrapper" ref={ref}>
        <MainContentWrapper>
          <ContentWrapper>
            <HeaderTitle>
              <HeaderIcon data-testid="vehicle-issues-card-title-icon">
                <VehicleIssuesIcon />
              </HeaderIcon>
              <HeaderText>{title || 'Vehicle Issues'}</HeaderText>
            </HeaderTitle>
            <TextContainer>
              <Text data-testid="asr-card-text">We&#39;re here to help! Submit a case in a few simple steps.</Text>
            </TextContainer>
          </ContentWrapper>
          <div>
            <Button data-testid="asr-card-button" size="small" onClick={launchASRForm}>
              Get Started
            </Button>
          </div>
        </MainContentWrapper>

        {hasConcerns && (
          <ConcernsWrapper data-testid="asr-concerns-wrapper">
            <HR />
            <Accordion
              headerTitle="Reported Concerns"
              icon={null}
              dataQa="asr-card"
              toggleStatusHandler={toggleStatusHandler}
            >
              <ConcernList data-testid="asr-card-concern-list">
                {reportedConcernsWithin14Days.map((concern: any) => (
                  <ConcernListItem
                    data-testid="asr-card-concern-list-item"
                    key={concern.id}
                    onClick={() => handleConcernClick(concern)}
                  >
                    Concern Reported on {formatDate(concern.createdOn, DateMasks.MMDDYYwSlash)} <ChevronIcon />
                  </ConcernListItem>
                ))}
              </ConcernList>
            </Accordion>
          </ConcernsWrapper>
        )}
        {showFootnote && (
          <FootNoteWrapper>
            <HR />
            <Note data-testid="asr-card-note-text">
              Coverage for cosmetic imperfections are reviewed on a case-by-case basis.
            </Note>
          </FootNoteWrapper>
        )}
      </ASRCardWrapper>
      {shouldShowReportedConcernsModal && (
        <ConcernReportedModal
          onModalCloseCallback={() => setShouldShowReportedConcernsModal(false)}
          reportedConcernData={selectedConcern}
        />
      )}
      {shouldShowCreateConcernModal && (
        <ReportConcernModal
          onModalCloseCallback={() => {
            setShouldShowCreateConcernModal(false);
            // we want to perform navigation back to the page
            // to pick up the new data, a reload will pull from
            // session storage
            // eslint-disable-next-line no-self-assign
            window.location.href = window.location.href;
          }}
          reportedConcerns={reportedConcernsWithin14Days}
        />
      )}
    </>
  );
};

export default ASRCard;
